import {
    Box,
    Button,
    Form,
    FormGroup,
    Heading,
    IconButton,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { capitalize } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { getFeatureFlagData, getFeatureFlagDefaultData } from '@common/api/featureFlags/getFeatureFlagData';
import { ManagerAuthorization } from '@common/enums';
import { type FeatureFlag, FeatureFlagModelDataTypes } from '@common/interfaces/featureFlag';
import { featureFlagSchema } from '@common/validation/featureFlag';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getSelectedLanguage } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
    type ValidationErrors,
} from '@frontend/utils';

const emptyFeatureFlag: FeatureFlag = {
    availableInFrontend: false,
    data: {},
    description: '',
    enabled: false,
    id: '',
    type: FeatureFlagModelDataTypes.Boolean,
};

export function FeatureFlags() {
    const language = getSelectedLanguage();
    const [selectedFeatureFlag, setSelectedFeatureFlag] = useState<string>();
    const [featureFlag, setFeatureFlag] = useState<FeatureFlag>(emptyFeatureFlag);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
    const abortableFetch = useAbortableFetch();

    assertHasAuthorization(ManagerAuthorization.featureFlag);

    useEffect(() => {
        loadFlags();
    }, []);

    return (
        <PageTemplate>
            <Heading mb={8}>{language.featureFlags.header}</Heading>

            <Box
                display={'grid'}
                gap={8}
                mb={8}
            >
                <TableContainer style={{ overflow: 'initial' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{language.featureFlags.featureFlagActive}</TableHeader>
                                <TableHeader>{language.featureFlags.featureFlagKey}</TableHeader>
                                <TableHeader colSpan={3}>{language.featureFlags.featureFlagDescription}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {featureFlags.map((currentFeatureFlag) => (
                                <Fragment key={currentFeatureFlag.id}>
                                    {currentFeatureFlag.id === '' && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                style={{ padding: '0px' }}
                                            >
                                                <Box
                                                    backgroundColor={'neutral50'}
                                                    height={16}
                                                ></Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {currentFeatureFlag.id !== '' && (
                                        <TableRow>
                                            <TableCell>
                                                {currentFeatureFlag.id !== '' && (
                                                    <Switch
                                                        checked={currentFeatureFlag.enabled}
                                                        onChange={async (e) => {
                                                            await toggleFlag(currentFeatureFlag.id, e.target.checked);
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>{currentFeatureFlag.id}</TableCell>
                                            <TableCell>{currentFeatureFlag.description}</TableCell>
                                            <TableCell>
                                                <Text
                                                    color={'warning'}
                                                    fontWeight={'bold'}
                                                    size={'xs'}
                                                    whiteSpace="nowrap"
                                                >
                                                    {currentFeatureFlag.availableInFrontend
                                                        ? language.featureFlags.availableInFrontend
                                                        : ''}
                                                </Text>
                                            </TableCell>
                                            <TableCell>
                                                {selectedFeatureFlag === undefined && (
                                                    <IconButton
                                                        icon="edit"
                                                        onClick={async () =>
                                                            await selectFeatureFlag(currentFeatureFlag)
                                                        }
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {selectedFeatureFlag === currentFeatureFlag.id && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                style={{ padding: '0px' }}
                                            >
                                                <Box
                                                    backgroundColor={'white'}
                                                    padding={8}
                                                >
                                                    <Form submitOnEnter={false}>
                                                        <FormGroup
                                                            hasErrors={validationErrors.has('id')}
                                                            label={language.featureFlags.featureFlagKey}
                                                        >
                                                            {currentFeatureFlag.id === '' && (
                                                                <>
                                                                    <TextInput
                                                                        className="automation-feature-flag-name"
                                                                        error={getValidationErrorTranslation(
                                                                            validationErrors.get('id')
                                                                        )}
                                                                        onBlur={() => validateOne('id')}
                                                                        onChange={(e) => {
                                                                            onChangeValidation('id');
                                                                            setFeatureFlag({
                                                                                ...featureFlag,
                                                                                id: e.target.value.toUpperCase(),
                                                                            });
                                                                        }}
                                                                        value={featureFlag.id}
                                                                        variant="outline"
                                                                    />
                                                                    <Text
                                                                        marginTop={4}
                                                                        size={'xs'}
                                                                    >
                                                                        {language.featureFlags.featureFlagKeyHelper}
                                                                    </Text>
                                                                </>
                                                            )}
                                                            {currentFeatureFlag.id !== '' && (
                                                                <Box
                                                                    alignItems={'center'}
                                                                    display={'grid'}
                                                                    gap={2}
                                                                    gridTemplateColumns={'1fr min-content'}
                                                                >
                                                                    <Text fontWeight={'bold'}>{featureFlag.id}</Text>

                                                                    <IconButton
                                                                        icon="delete"
                                                                        onClick={async () =>
                                                                            await confirmDelete(currentFeatureFlag.id)
                                                                        }
                                                                    />
                                                                </Box>
                                                            )}
                                                        </FormGroup>

                                                        {selectedFeatureFlag === '' && (
                                                            <FormGroup
                                                                hasErrors={validationErrors.has('enabled')}
                                                                label={language.featureFlags.featureFlagActive}
                                                            >
                                                                <Switch
                                                                    checked={featureFlag.enabled}
                                                                    onChange={(e) => {
                                                                        setFeatureFlag({
                                                                            ...featureFlag,
                                                                            enabled: e.target.checked,
                                                                        });
                                                                    }}
                                                                />
                                                            </FormGroup>
                                                        )}

                                                        <FormGroup
                                                            hasErrors={validationErrors.has('description')}
                                                            label={language.featureFlags.featureFlagDescription}
                                                        >
                                                            <TextInput
                                                                className="automation-feature-flag-description"
                                                                error={getValidationErrorTranslation(
                                                                    validationErrors.get('description')
                                                                )}
                                                                onBlur={() => validateOne('description')}
                                                                onChange={(e) => {
                                                                    onChangeValidation('description');
                                                                    setFeatureFlag({
                                                                        ...featureFlag,
                                                                        description: e.target.value,
                                                                    });
                                                                }}
                                                                value={featureFlag.description}
                                                                variant="outline"
                                                            />
                                                            <Text
                                                                marginTop={4}
                                                                size={'xs'}
                                                            >
                                                                {language.featureFlags.featureFlagDescriptionHelper}
                                                            </Text>
                                                        </FormGroup>

                                                        <FormGroup
                                                            hasErrors={validationErrors.has('type')}
                                                            label={language.featureFlags.featureFlagType}
                                                        >
                                                            {currentFeatureFlag.id === '' && (
                                                                <Select
                                                                    className="automation-feature-flag-type"
                                                                    error={getValidationErrorTranslation(
                                                                        validationErrors.get('type')
                                                                    )}
                                                                    onBlur={() => validateOne('type')}
                                                                    onChange={(option) => {
                                                                        const type =
                                                                            option?.value ??
                                                                            FeatureFlagModelDataTypes.String;

                                                                        setFeatureFlag({
                                                                            ...featureFlag,
                                                                            data: getFeatureFlagDefaultData(type),
                                                                            type,
                                                                        } as FeatureFlag);
                                                                    }}
                                                                    options={Object.values(
                                                                        FeatureFlagModelDataTypes
                                                                    ).map((type) => ({
                                                                        label: capitalize(type),
                                                                        value: type,
                                                                    }))}
                                                                    value={{
                                                                        label: capitalize(featureFlag.type ?? ''),
                                                                        value: featureFlag.type,
                                                                    }}
                                                                />
                                                            )}
                                                            {currentFeatureFlag.id !== '' && (
                                                                <Text fontWeight={'bold'}>
                                                                    {capitalize(featureFlag.type)}
                                                                </Text>
                                                            )}
                                                        </FormGroup>

                                                        {featureFlag.type === FeatureFlagModelDataTypes.String && (
                                                            <FormGroup
                                                                hasErrors={validationErrors.has('data.value')}
                                                                label={language.featureFlags.featureFlagTypeValue}
                                                            >
                                                                <TextInput
                                                                    className="automation-feature-flag-string-data"
                                                                    onChange={({ target: { value } }) => {
                                                                        setFeatureFlag({
                                                                            ...featureFlag,
                                                                            data: { value },
                                                                        });
                                                                    }}
                                                                    value={getFeatureFlagData(featureFlag).value}
                                                                    variant="outline"
                                                                />
                                                            </FormGroup>
                                                        )}

                                                        {featureFlag.type === FeatureFlagModelDataTypes.Array && (
                                                            <FormGroup
                                                                hasErrors={validationErrors.has('data.value')}
                                                                label={language.featureFlags.featureFlagTypeValue}
                                                            >
                                                                <Box
                                                                    display={'grid'}
                                                                    gap={2}
                                                                >
                                                                    {getFeatureFlagData(featureFlag).value.map(
                                                                        (value, key) => (
                                                                            <Box
                                                                                alignItems={'center'}
                                                                                display={'grid'}
                                                                                gap={2}
                                                                                gridTemplateColumns={'1fr min-content'}
                                                                                key={key}
                                                                            >
                                                                                <TextInput
                                                                                    className="automation-feature-flag-array-data"
                                                                                    onChange={({ target }) => {
                                                                                        setFeatureFlag({
                                                                                            ...featureFlag,
                                                                                            data: {
                                                                                                value: (
                                                                                                    featureFlag.data
                                                                                                        ?.value ?? []
                                                                                                ).map(
                                                                                                    (
                                                                                                        entryValue,
                                                                                                        index
                                                                                                    ) =>
                                                                                                        key === index
                                                                                                            ? // eslint-disable-next-line @stylistic/max-len
                                                                                                              target.value
                                                                                                            : entryValue
                                                                                                ),
                                                                                            },
                                                                                        });
                                                                                    }}
                                                                                    value={value.toString() ?? ''}
                                                                                    variant="outline"
                                                                                />
                                                                                <IconButton
                                                                                    icon="delete"
                                                                                    onClick={() => {
                                                                                        setFeatureFlag({
                                                                                            ...featureFlag,
                                                                                            data: {
                                                                                                value: (
                                                                                                    featureFlag.data
                                                                                                        ?.value ?? []
                                                                                                ).filter(
                                                                                                    (_, index) =>
                                                                                                        key !== index
                                                                                                ),
                                                                                            },
                                                                                        });
                                                                                    }}
                                                                                    size="small"
                                                                                ></IconButton>
                                                                            </Box>
                                                                        )
                                                                    )}
                                                                    <IconButton
                                                                        icon="add"
                                                                        isDisabled={
                                                                            !!getFeatureFlagData(
                                                                                featureFlag
                                                                            ).value.some((entry) => entry === '')
                                                                        }
                                                                        onClick={async () => {
                                                                            setFeatureFlag({
                                                                                ...featureFlag,
                                                                                data: {
                                                                                    value: [
                                                                                        ...getFeatureFlagData(
                                                                                            featureFlag
                                                                                        ).value,
                                                                                        '',
                                                                                    ],
                                                                                },
                                                                            });
                                                                        }}
                                                                        size="small"
                                                                        variant="filled"
                                                                    ></IconButton>
                                                                </Box>
                                                            </FormGroup>
                                                        )}

                                                        <FormGroup label={language.featureFlags.availableInFrontend}>
                                                            <Switch
                                                                checked={featureFlag.availableInFrontend}
                                                                onChange={(e) => {
                                                                    setFeatureFlag({
                                                                        ...featureFlag,
                                                                        availableInFrontend: e.target.checked,
                                                                    });
                                                                }}
                                                            />
                                                        </FormGroup>

                                                        <FormGroup>
                                                            <Box
                                                                display={'flex'}
                                                                gap={4}
                                                                mt={4}
                                                            >
                                                                {selectedFeatureFlag === '' && (
                                                                    <Button onClick={saveFlag}>
                                                                        {language.common.saveButton}
                                                                    </Button>
                                                                )}
                                                                {selectedFeatureFlag !== '' && (
                                                                    <Button onClick={updateFlag}>
                                                                        {language.common.saveButton}
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    onClick={() => {
                                                                        if (selectedFeatureFlag !== '') {
                                                                            deselectFeatureFlag();
                                                                        } else {
                                                                            confirmDelete(currentFeatureFlag.id, false);
                                                                        }
                                                                    }}
                                                                    variant="outline"
                                                                >
                                                                    {language.common.cancel}
                                                                </Button>
                                                            </Box>
                                                        </FormGroup>
                                                    </Form>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box>
                    <Button
                        isDisabled={featureFlags.some((entry) => entry.id === '')}
                        onClick={async () => await addFlag()}
                    >
                        {language.featureFlags.addFeatureFlag}
                    </Button>
                </Box>
            </Box>
        </PageTemplate>
    );

    async function loadFlags() {
        const url = '/api/featureFlags';
        const result = await abortableFetch.fetchJson(url, 'GET');
        deselectFeatureFlag();
        setFeatureFlags(result.json.items);
    }

    function clearFormValidation() {
        setValidationErrors(emptyValidationErrors);
    }

    function validateOne(name: keyof FeatureFlag) {
        setValidationErrors(updateValidationErrorsForOne(featureFlagSchema, featureFlag, validationErrors, name));
    }

    function onChangeValidation(key: string) {
        if (validationErrors.get(key)) {
            validateOne(key as keyof FeatureFlag);
        }
    }

    async function toggleFlag(id: string, enabled: boolean) {
        if (!window.confirm(enabled ? language.featureFlags.confirmToggleOn : language.featureFlags.confirmToggleOff)) {
            return;
        }

        const url = `/api/featureFlags/${id}`;

        const currentFeatureFlag = featureFlags.find((flag) => flag.id === id);

        if (!currentFeatureFlag) {
            return;
        }

        await abortableFetch.fetchJson(url, 'PUT', { enabled });
        await loadFlags();
    }

    async function selectFeatureFlag(currentFeatureFlag: FeatureFlag) {
        setFeatureFlag(currentFeatureFlag);
        setSelectedFeatureFlag(currentFeatureFlag.id);
        clearFormValidation();
    }

    function deselectFeatureFlag() {
        setFeatureFlag(emptyFeatureFlag);
        setSelectedFeatureFlag(undefined);
        clearFormValidation();
    }

    async function updateFlag() {
        const { errors } = validate(featureFlagSchema, featureFlag);

        if (errors.size > 0) {
            setValidationErrors(errors);

            return;
        }

        const { id, ...data } = featureFlag;

        const url = `/api/featureFlags/${id}`;

        await abortableFetch.fetchJson(url, 'PUT', data);
        await loadFlags();
    }

    async function confirmDelete(id: string, showConfirmation = true) {
        if (showConfirmation && !window.confirm(language.featureFlags.confirmDelete)) {
            return;
        }

        if (id !== '') {
            const url = `/api/featureFlags/${id}`;

            await abortableFetch.fetchJson(url, 'DELETE');
        }

        await loadFlags();
    }

    async function addFlag() {
        const newFeatureFlag = { ...emptyFeatureFlag };
        setFeatureFlags([...featureFlags, newFeatureFlag]);
        setFeatureFlag(newFeatureFlag);
        setSelectedFeatureFlag(newFeatureFlag.id);
        clearFormValidation();
    }

    async function saveFlag() {
        const { errors } = validate(featureFlagSchema, featureFlag);

        if (errors.size > 0) {
            setValidationErrors(errors);

            return;
        }

        const url = '/api/featureFlags';

        await abortableFetch.fetchJson(url, 'POST', featureFlag);
        setFeatureFlag(emptyFeatureFlag);
        await loadFlags();
    }
}
